/**
 * Die Akte — der Einstieg.
 *
 * Eigener Kontostand groß, das nächste Ereignis mit Countdown, der
 * "Ich bin da"-Knopf nur im Zeitfenster der Sitzung.
 */
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, ApiFehler } from '../api/client.js'
import { formatDatumZeit, formatRestzeit, formatSchnaepse } from '../api/format.js'
import { useLaden } from '../api/laden.js'
import { useSitzung } from '../api/sitzung.js'
import type { Konto, SitzungsEvent } from '../api/typen.js'
import {
  Abschnitt,
  Amtsknopf,
  Fehlermeldung,
  Haarlinie,
  Laufvermerk,
  LeererZustand,
} from '../bausteine/Bausteine.js'
import { PushSchalter } from '../bausteine/Push.js'
import { SpendenTeller } from '../bausteine/Spenden.js'

/** Der Check-in öffnet drei Stunden vor Beginn — wie im Server. */
const CHECKIN_VORLAUF_MS = 3 * 60 * 60 * 1000

export function Dashboard() {
  const { mitglied } = useSitzung()
  const navigiere = useNavigate()
  const [jetzt, setJetzt] = useState(Date.now())
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  const konto = useLaden(() => api.get<Konto>('/api/konto'))
  const events = useLaden(() => api.get<SitzungsEvent[]>('/api/events'))

  useEffect(() => {
    const takt = setInterval(() => setJetzt(Date.now()), 1000)
    return () => clearInterval(takt)
  }, [])

  const kommende = (events.daten ?? [])
    .filter((e) => e.status !== 'abgeschlossen' && Date.parse(e.endsAt) > jetzt)
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))
  const naechstes = kommende[0]

  const fensterOffen =
    naechstes !== undefined &&
    jetzt >= Date.parse(naechstes.startsAt) - CHECKIN_VORLAUF_MS &&
    jetzt <= Date.parse(naechstes.endsAt)

  async function binDa() {
    if (!naechstes) return
    setLaeuft(true)
    setFehler(null)
    try {
      await api.post(`/api/events/${naechstes.id}/check-in`, {})
      navigiere(`/termine/${naechstes.id}`)
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : 'Der Check-in ist fehlgeschlagen.')
    } finally {
      setLaeuft(false)
    }
  }

  if (konto.laedt || events.laedt) return <Laufvermerk />

  return (
    <>
      <section className="pt-10 pb-6 text-center">
        <p className="protokoll">Offene Schuld · {mitglied?.name}</p>
        <p className="urteil mt-3">
          {konto.daten ? formatSchnaepse(konto.daten.offeneUnits, konto.daten.unitCl) : '—'}
        </p>
        {konto.daten && konto.daten.offeneUnits === 0 && (
          <p className="mt-3 text-sm text-akte/70">Keine offenen Posten. Die Kammer ruht.</p>
        )}
        <div className="mt-4">
          <Link
            to="/konto"
            className="protokoll normal-case underline decoration-messing/40 underline-offset-4 hover:text-messing"
          >
            Zum vollständigen Ledger
          </Link>
        </div>
      </section>

      <Haarlinie />

      <Abschnitt
        titel="Nächster Termin"
        aktion={
          <Link to="/termine/neu" className="protokoll normal-case text-messing hover:text-lampe">
            + Ansetzen
          </Link>
        }
      >
        {!naechstes ? (
          <LeererZustand text="Kein Termin angesetzt. Die Kammer wartet auf Anträge." />
        ) : (
          <div className="border border-eiche bg-bank p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-lg leading-snug">{naechstes.title}</h2>
                <p className="protokoll mt-1 normal-case">
                  {formatDatumZeit(naechstes.startsAt)} · {naechstes.location}
                </p>
              </div>
              {naechstes.type === 'sonderschnapsevent' && (
                <span className="stempel stempel-vollstreckt">Sonder</span>
              )}
            </div>

            <p className="mt-4 font-akte text-2xl tabular-nums text-lampe">
              {Date.parse(naechstes.startsAt) > jetzt
                ? formatRestzeit(Date.parse(naechstes.startsAt), jetzt)
                : 'Die Sitzung läuft.'}
            </p>
            <p className="protokoll mt-0.5 normal-case">
              {Date.parse(naechstes.startsAt) > jetzt ? 'bis zum Aufruf der Sache' : `seit ${formatDatumZeit(naechstes.startsAt)}`}
            </p>

            <div className="mt-4 flex flex-col gap-2">
              <Amtsknopf onClick={() => void binDa()} disabled={!fensterOffen || laeuft}>
                {laeuft ? 'Wird eingetragen …' : 'Ich bin da'}
              </Amtsknopf>
              {!fensterOffen && (
                <p className="protokoll normal-case">
                  Der Check-in öffnet drei Stunden vor Beginn.
                </p>
              )}
              <Link
                to={`/termine/${naechstes.id}`}
                className="py-1 text-center font-sans text-sm text-messing underline-offset-4 hover:underline"
              >
                Zur Verhandlung
              </Link>
            </div>
            <Fehlermeldung text={fehler} />
          </div>
        )}
      </Abschnitt>

      <Abschnitt titel="Meldungen der Kammer">
        <PushSchalter />
      </Abschnitt>

      <Abschnitt titel="Weitere Amtsstuben">
        <div className="grid grid-cols-2 gap-2.5">
          <Link
            to="/schande"
            className="border border-eiche bg-bank px-4 py-3 text-center text-sm text-lampe transition-colors hover:border-messing"
          >
            Hall of Shame
          </Link>
          <Link
            to="/verwaltung"
            className="border border-eiche bg-bank px-4 py-3 text-center text-sm text-lampe transition-colors hover:border-messing"
          >
            Verwaltung
          </Link>
          <Link
            to="/feedback"
            className="col-span-2 border border-eiche bg-bank px-4 py-3 text-center text-sm text-lampe transition-colors hover:border-messing"
          >
            Eingaben an die Kammer — Feedback &amp; Wünsche
          </Link>
        </div>
        <SpendenTeller />
      </Abschnitt>
    </>
  )
}
